import { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { AppLayout } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { ListChecks, Plus, Pencil, Trash2, Loader2, MapPin } from "lucide-react";

const ListDialog = ({ open, onOpenChange, initial, partners, onSaved }) => {
  const [name, setName] = useState("");
  const [ids, setIds] = useState([]);
  const [prov, setProv] = useState("");
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) { setName(initial?.name || ""); setIds(initial?.partner_ids || []); setProv(""); setQ(""); } }, [open, initial]);
  const province = useMemo(() => [...new Set(partners.map((p) => p.provincia).filter(Boolean))].sort(), [partners]);
  const addProvincia = () => {
    if (!prov) return;
    const add = partners.filter((p) => p.provincia === prov).map((p) => p.id);
    setIds((cur) => [...new Set([...cur, ...add])]);
    toast.success(`${add.length} partner della provincia ${prov} aggiunti`);
  };
  const toggle = (id) => setIds((cur) => (cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]));
  const visible = partners.filter((p) => !q || `${p.name} ${p.provincia || ""}`.toLowerCase().includes(q.toLowerCase()));

  const save = async () => {
    if (!name.trim()) return toast.error("Inserisci il nome della lista");
    setSaving(true);
    try {
      if (initial?.id) await api.put(`/partner-lists/${initial.id}`, { name, partner_ids: ids });
      else await api.post("/partner-lists", { name, partner_ids: ids });
      toast.success(initial?.id ? "Lista aggiornata" : "Lista creata");
      onOpenChange(false); onSaved();
    } catch (err) { toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{initial?.id ? "Modifica lista" : "Nuova lista partner"}</DialogTitle>
          <DialogDescription>Dai un nome alla lista (es. una provincia) e scegli i partner che ne fanno parte.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="nx-label">Nome lista *</Label>
            <Input data-testid="list-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="Es. Milano" className="mt-1.5" />
          </div>
          <div>
            <Label className="nx-label">Aggiungi tutti i partner di una provincia</Label>
            <div className="flex gap-2 mt-1.5">
              <Select value={prov} onValueChange={setProv}>
                <SelectTrigger data-testid="list-provincia-select" className="flex-1"><SelectValue placeholder={province.length ? "Seleziona provincia" : "Nessuna provincia nei partner"} /></SelectTrigger>
                <SelectContent>{province.map((pv) => <SelectItem key={pv} value={pv}>{pv} ({partners.filter((p) => p.provincia === pv).length})</SelectItem>)}</SelectContent>
              </Select>
              <Button type="button" variant="outline" data-testid="list-add-provincia" onClick={addProvincia} disabled={!prov} className="gap-1.5"><MapPin className="h-4 w-4" /> Aggiungi</Button>
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <Label className="nx-label">Partner nella lista ({ids.length})</Label>
              <button type="button" data-testid="list-clear" className="text-xs text-slate-500 hover:underline" onClick={() => setIds([])}>Svuota</button>
            </div>
            <Input data-testid="list-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca partner…" className="h-8 text-sm mb-2" />
            <div className="max-h-60 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-100">
              {visible.map((p) => (
                <label key={p.id} data-testid={`list-partner-${p.id}`} className={`flex items-center gap-3 px-3 py-2 cursor-pointer text-sm ${ids.includes(p.id) ? "bg-primary/5" : "hover:bg-slate-50"}`}>
                  <Checkbox checked={ids.includes(p.id)} onCheckedChange={() => toggle(p.id)} />
                  <span className="flex-1 text-slate-800">{p.name}</span>
                  {p.provincia && <span className="text-xs text-slate-400">{p.provincia}</span>}
                </label>
              ))}
              {visible.length === 0 && <p className="text-xs text-slate-400 p-3">Nessun partner trovato.</p>}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="list-save" onClick={save} disabled={saving} className="w-full gap-2">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva lista"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function PartnerListsPage() {
  const [lists, setLists] = useState(null);
  const [partners, setPartners] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    const [l, p] = await Promise.all([api.get("/partner-lists"), api.get("/partners")]);
    setLists(l.data); setPartners(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  const nameOf = useMemo(() => Object.fromEntries(partners.map((p) => [p.id, p.name])), [partners]);

  const remove = async (l) => {
    if (!window.confirm(`Eliminare la lista "${l.name}"?`)) return;
    try { await api.delete(`/partner-lists/${l.id}`); toast.success("Lista eliminata"); load(); }
    catch (err) { toast.error("Errore", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
  };

  return (
    <AppLayout>
      <div className="max-w-5xl mx-auto nx-fade-in pb-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold font-heading text-slate-900 tracking-tight flex items-center gap-2"><ListChecks className="h-7 w-7 text-primary" /> Liste Partner</h1>
            <p className="text-sm text-slate-500 mt-1">Gruppi di partner (es. per provincia) da usare per l'invio mirato di trasporti ed eventi</p>
          </div>
          <Button data-testid="new-list-button" className="gap-2" onClick={() => { setEditing(null); setOpen(true); }}><Plus className="h-4 w-4" /> Nuova lista</Button>
        </div>
        {lists === null ? <Skeleton className="h-40 w-full" /> : lists.length === 0 ? (
          <Card className="p-10 text-center border-slate-200"><p className="text-sm text-slate-500">Nessuna lista. Crea la prima lista partner.</p></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {lists.map((l) => (
              <Card key={l.id} data-testid={`list-card-${l.id}`} className="p-5 border-slate-200">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div>
                    <h3 className="font-semibold text-slate-800 font-heading">{l.name}</h3>
                    <p className="text-xs text-slate-400">{l.partner_ids.length} partner</p>
                  </div>
                  <div className="flex gap-1">
                    <Button data-testid={`list-edit-${l.id}`} variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-primary" onClick={() => { setEditing(l); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                    <Button data-testid={`list-delete-${l.id}`} variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={() => remove(l)}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {l.partner_ids.map((id) => <span key={id} className="inline-flex rounded-full bg-slate-100 px-2.5 py-0.5 text-xs text-slate-700">{nameOf[id] || "—"}</span>)}
                </div>
              </Card>
            ))}
          </div>
        )}
        <ListDialog open={open} onOpenChange={setOpen} initial={editing} partners={partners} onSaved={load} />
      </div>
    </AppLayout>
  );
}
